import React, { useState } from 'react';
import { Bot, Key, Globe, Thermometer, Hash, FileText, Save, CheckCircle2 } from 'lucide-react';
import { LLM_PRESETS } from '../constants';
import { SectionTitle, FormInput, FormTextArea } from './Shared';

export default function SettingsPage({ userRole }) {
  const [config, setConfig] = useState({
    apiKey: '',
    baseUrl: '',
    model: '',
    temperature: 0.7,
    maxTokens: 4096,
    promptTemplate: '',
  });
  const [saved, setSaved] = useState(false);

  function applyPreset(preset) {
    setConfig({
      ...config,
      baseUrl: preset.baseUrl,
      model: preset.model,
      temperature: preset.temperature,
      maxTokens: preset.maxTokens,
    });
  }

  function handleSave() {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  if (!userRole?.canConfigLLM) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <Bot className="w-16 h-16 mb-4" />
        <p>您没有权限访问系统配置</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <h1 className="text-xl font-bold text-slate-800">系统配置</h1>

      <div className="card p-6 space-y-6">
        <SectionTitle icon={Bot} title="LLM / Agent 接口配置" />
        <p className="text-sm text-slate-500">配置大语言模型接口，用于任务中心的AI辅助功能。以下信息仅保存在本地内存中，刷新页面后将重置。</p>

        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-slate-500">快速选择预设：</span>
          {LLM_PRESETS.map(p => (
            <button key={p.name} onClick={() => applyPreset(p)} className="px-2 py-1 text-xs rounded border border-slate-200 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 transition-colors">
              {p.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">API Key</label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="password" className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="sk-..." value={config.apiKey} onChange={e => setConfig({ ...config, apiKey: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Base URL</label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="https://api.openai.com/v1" value={config.baseUrl} onChange={e => setConfig({ ...config, baseUrl: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Model Name</label>
            <input type="text" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="gpt-4" value={config.model} onChange={e => setConfig({ ...config, model: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Temperature ({config.temperature})</label>
            <input type="range" min="0" max="2" step="0.1" className="w-full" value={config.temperature} onChange={e => setConfig({ ...config, temperature: parseFloat(e.target.value) })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Max Tokens</label>
            <input type="number" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" value={config.maxTokens} onChange={e => setConfig({ ...config, maxTokens: parseInt(e.target.value) })} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Prompt Template</label>
          <textarea className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y" rows={6} placeholder="输入系统提示词模板..." value={config.promptTemplate} onChange={e => setConfig({ ...config, promptTemplate: e.target.value })} />
        </div>

        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-100">
          {saved && <span className="text-sm text-emerald-600 flex items-center"><CheckCircle2 className="w-4 h-4 mr-1" /> 已保存</span>}
          <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center">
            <Save className="w-4 h-4 mr-2" /> 保存配置
          </button>
        </div>
      </div>

      <div className="card p-6 space-y-4">
        <SectionTitle icon={FileText} title="接口预留说明" />
        <div className="text-sm text-slate-600 space-y-2">
          <p>本系统已预留以下接口对接点，供后续模块扩展使用：</p>
          <ul className="list-disc list-inside space-y-1 text-slate-500">
            <li><strong>系统底座 01</strong>：用户认证、权限校验、组织架构同步</li>
            <li><strong>ASIN 工作台 / Agent 模块</strong>：任务创建时自动关联ASIN数据、AI输出结果回写</li>
            <li><strong>审批中心 / 异常中心</strong>：高风险任务自动推送、异常状态联动</li>
            <li><strong>复盘中心</strong>：任务完成后自动触发复盘流程、数据归档</li>
            <li><strong>总整合导航</strong>：统一路由跳转、面包屑同步、消息通知</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
